// Match a patient to rows on a study's enrolled list (studies_copy/{id}/patients)
// by name + date of birth, with a confidence percentage.
//
// The master list is typed by hand, so names drift: a missing or extra middle
// name, "Last, First", case, a nickname, a typo. This reuses the tested
// compareNames / normalizeDob from patientDuplicateMatch.js and turns the result
// into a score staff can read:
//
//   100%  DOB matches and the name is the same (ignoring case, spacing,
//         punctuation, suffixes, and a middle initial vs the full middle name)
//    95%  the same, except a middle name/initial is on only one record
//    89%  both records hold the same single name and nothing else (no last
//         name on either). As strong as a one-word name gets, but kept below
//         the 90% auto-move line so it always gets a human look. (Enrolled
//         list only: scoreMasterListRow.)
//   <=89% anything looser: nickname, typo, initial-only first or last name,
//         reversed order, different middle names, a single name on one side.
//         Capped below the 90% auto-move line so these always get a human look.
//
// The DOB must match exactly; an unreadable DOB never matches. Runs entirely
// in the app (or the backfill script); no patient data goes anywhere else.
//
// Import-free apart from its sibling, with explicit ".js" extensions, so the
// plain-Node backfill script can import it as well as the app.

import { compareNames, normalizeDob, ONE_NAME_BOTH_REASON } from "./patientDuplicateMatch.js";

const EXACT = "Name matches exactly";
const MIDDLE_ONE_SIDE = "Middle name/initial present on only one record";

// Turn compareNames' { score, reasons } into the percentage above.
export const confidenceFromNameMatch = (match) => {
  if (!match) return 0;
  const reasons = match.reasons || [];
  if (reasons.length === 1 && reasons[0] === EXACT) return 100;
  if (reasons.length === 1 && reasons[0] === MIDDLE_ONE_SIDE) return 95;
  return Math.min(89, Math.max(0, Math.round((match.score || 0) * 100)));
};

const rowName = (row) => (row && (row.NAME || row.name)) || "";
const rowDob = (row) => (row && (row.DOB || row.dob)) || "";

/**
 * Score one patient against one master-list row.
 * @returns {{score:number, reasons:string[]} | null} null when not the same person.
 */
export const scoreMasterListRow = (patient, row) => {
  const dobKey = normalizeDob(patient && patient.dob);
  if (!dobKey || normalizeDob(rowDob(row)) !== dobKey) return null;
  const match = compareNames(patient && patient.name, rowName(row));
  if (!match) return null;
  // The 89% one-name case is scored here, not in confidenceFromNameMatch,
  // so PFT Uploads (which shares that function) scores exactly as before.
  const oneNameBoth = match.reasons.length === 1 && match.reasons[0] === ONE_NAME_BOTH_REASON;
  return { score: oneNameBoth ? 89 : confidenceFromNameMatch(match), reasons: match.reasons };
};

/**
 * Every row on a list that could be this patient, best first.
 * @param {{name:string, dob:string}} patient
 * @param {Array<object>} rows - master-list rows ({NAME, DOB, ...})
 * @returns {Array<{row, score, reasons}>}
 */
export const findMasterListMatches = (patient, rows) =>
  (rows || [])
    .map((row) => {
      const m = scoreMasterListRow(patient, row);
      return m ? { row, ...m } : null;
    })
    .filter(Boolean)
    .sort((a, b) => b.score - a.score);
